import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications, LOGO_URL } from '../context/NotificationContext';
import {
  Wallet, BarChart3, Bot, ShieldAlert, Bell,
  LogOut, Target, LayoutDashboard, MapPin, CalendarPlus,
  CheckCircle, Building2, X, Gift, Calculator
} from 'lucide-react';

const playerNav = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/wallet', label: 'Wallet', icon: Wallet },
  { to: '/redeem', label: 'Redeem', icon: Gift },
  { to: '/planner', label: 'Planner', icon: Calculator },
  { to: '/sessions', label: 'Sessions', icon: Target },
  { to: '/checkin', label: 'Check-In', icon: CheckCircle },
];

const coachNav = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/coach', label: 'Coach Hub', icon: CalendarPlus },
  { to: '/wallet', label: 'Wallet', icon: Wallet },
  { to: '/redeem', label: 'Redeem', icon: Gift },
  { to: '/planner', label: 'Planner', icon: Calculator },
  { to: '/sessions', label: 'Sessions', icon: Target },
];

const venueNav = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/venue', label: 'Venue Hub', icon: Building2 },
  { to: '/wallet', label: 'Wallet', icon: Wallet },
  { to: '/redeem', label: 'Redeem', icon: Gift },
  { to: '/planner', label: 'Planner', icon: Calculator },
];

const adminNav = [
  { to: '/admin', label: 'Economy Health', icon: BarChart3 },
  { to: '/admin/fraud', label: 'Fraud Signals', icon: ShieldAlert },
  { to: '/admin/agents', label: 'Agent Logs', icon: Bot },
  { to: '/planner', label: 'Planner', icon: Calculator },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markAllRead } = useNotifications();
  const navigate = useNavigate();
  const [showNotifs, setShowNotifs] = useState(false);

  const role = user?.role;
  const navItems = role === 'admin' ? adminNav :
    role === 'coach' ? coachNav :
    role === 'venue_owner' ? venueNav : playerNav;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#020408] flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-white/5 bg-slate-950/40 backdrop-blur-sm">
        <div className="p-5 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="SetPlay" className="w-9 h-9 rounded-lg" />
            <div>
              <span className="font-barlow text-lg font-bold text-white tracking-tight">SetPlay</span>
              <span className="text-[10px] text-lime-400 font-mono ml-1.5 uppercase tracking-widest">A-ITE</span>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1" data-testid="sidebar-nav">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/' || item.to === '/admin'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-manrope transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-600/15 text-blue-400 border border-blue-500/20'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 mb-3 px-1">
            <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 font-barlow font-bold text-sm">
              {user?.name?.charAt(0) || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-slate-500 text-xs capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="SetPlay" className="w-7 h-7 rounded-md" />
            <span className="font-barlow text-base font-bold text-white">SetPlay</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNotifs(!showNotifs)}
              className="relative text-slate-400 hover:text-white p-1"
              data-testid="mobile-notif-btn"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-pink-500 text-[9px] text-white flex items-center justify-center font-bold">{unreadCount}</span>
              )}
            </button>
            <button onClick={handleLogout} className="text-slate-400 hover:text-white p-1">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-lg border-t border-white/5">
        <div className="flex items-center justify-around py-2">
          {navItems.slice(0, 5).map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/' || item.to === '/admin'}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1.5 text-[10px] font-manrope transition-colors ${
                  isActive ? 'text-blue-400' : 'text-slate-500'
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 md:overflow-y-auto min-h-screen pt-14 pb-20 md:pt-0 md:pb-0 relative">
        {/* Desktop Notification Bell */}
        <div className="hidden md:flex absolute top-4 right-8 z-40">
          <button
            onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs) markAllRead(); }}
            className="relative p-2 rounded-lg hover:bg-white/5 transition-colors"
            data-testid="notif-bell-btn"
          >
            <Bell size={20} className="text-slate-400" />
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-pink-500 text-[9px] text-white flex items-center justify-center font-bold animate-pulse">{unreadCount}</span>
            )}
          </button>
        </div>

        {/* Notification Panel */}
        {showNotifs && (
          <div className="fixed md:absolute top-14 md:top-12 right-0 md:right-4 z-50 w-full md:w-96 max-h-[70vh] bg-slate-950/95 backdrop-blur-xl border border-white/10 md:rounded-xl shadow-2xl overflow-hidden" data-testid="notif-panel">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h4 className="font-barlow text-sm font-bold text-white uppercase tracking-wider">Notifications</h4>
              <button onClick={() => setShowNotifs(false)} className="text-slate-400 hover:text-white">
                <X size={16} />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">No notifications yet</div>
              ) : (
                notifications.slice(0, 15).map((n, i) => (
                  <div key={n.id || i} className={`px-4 py-3 border-b border-white/5 ${!n.read ? 'bg-blue-500/5' : ''} hover:bg-white/5 transition-colors`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        n.type === 'fraud_alert' ? 'bg-pink-400' :
                        n.type === 'checkin_success' ? 'bg-lime-400' :
                        n.type === 'booking_confirmed' ? 'bg-blue-400' :
                        'bg-slate-400'
                      }`} />
                      <span className="text-white text-sm font-medium">{n.title}</span>
                    </div>
                    <p className="text-slate-400 text-xs pl-3.5">{n.message}</p>
                    <p className="text-slate-600 text-[10px] font-mono pl-3.5 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
